import { useState, useEffect, useCallback } from "react";
import { Menu, X, Phone, Mail, ChevronDown } from "lucide-react";
import { usePageContext } from "vike-react/usePageContext";
import { navigate, prefetch } from "vike/client/router";
import { useLanguage } from "@/i18n/LanguageContext";
import type { Language } from "@/i18n/translations";
import { routes, getRoute } from "@/i18n/routes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPartsOpen, setIsPartsOpen] = useState(false);
  const [isMobilePartsOpen, setIsMobilePartsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const pageContext = usePageContext();
  const { language, t } = useLanguage();

  const homePath = `/${language}`;
  const isCurrentlyOnHome = pageContext.urlPathname.replace(/\/$/, "") === homePath;

  const getCurrentRouteKey = useCallback((currentPathname: string, currentLang: Language): string | null => {
    // Standardize: remove trailing slash for comparison
    const cleanPath = currentPathname.replace(/\/$/, "");
    const pathWithoutLang = cleanPath.replace(`/${currentLang}/`, "").replace(`/${currentLang}`, "");

    if (pathWithoutLang === "" || pathWithoutLang === "/") return "home";

    // Check static routes
    for (const [key, translations] of Object.entries(routes)) {
        if (key === 'services') continue;
        if ((translations as any)[currentLang] === pathWithoutLang) {
            return key;
        }
    }

    // Check services
    for (const [key, translations] of Object.entries(routes.services)) {
        if ((translations as any)[currentLang] === pathWithoutLang) {
            return `services.${key}`;
        }
    }

    // Special case for catalog/:slug
    const catalogPrefix = routes.catalog[currentLang];
    if (pathWithoutLang.startsWith(catalogPrefix + "/")) {
       return "productDetail";
    }

    return null;
  }, []);

  const handleLanguageChange = (newLang: Language) => {
    if (newLang === language) return;
    sessionStorage.setItem("isLanguageSwitch", "true");

    const currentRouteKey = getCurrentRouteKey(pageContext.urlPathname, language);
    let newPath = `/${newLang}`;

    if (currentRouteKey && currentRouteKey !== 'home') {
        if (currentRouteKey === 'productDetail') {
             // Correctly extract slug regardless of trailing slashes
             const segments = pageContext.urlPathname.split('/').filter(Boolean);
             const currentSlug = segments[segments.length - 1];
             const newBasePath = routes.catalog[newLang];
             newPath = `/${newLang}/${newBasePath}/${currentSlug}`;
        } else {
             const routePath = getRoute(currentRouteKey, newLang);
             newPath = routePath ? `/${newLang}/${routePath}` : `/${newLang}`;
        }
    }

    // Strict removal of trailing slash
    newPath = newPath.replace(/\/+$/, "");
    if (newPath === "") newPath = "/";

    navigate(newPath + window.location.search, { keepScrollPosition: true });
  };

  const scrollToElement = (hash: string) => {
    const id = hash.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      window.history.replaceState(null, "", hash);
    }
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    if (isCurrentlyOnHome) {
      e.preventDefault();
      scrollToElement(hash);
      setIsMenuOpen(false);
    }
  };

  const handleNavMouseEnter = () => {
    if (!isCurrentlyOnHome) {
      prefetch(homePath);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY <= 0) {
        setIsHidden(false);
      } else if (currentScrollY > lastScrollY && currentScrollY < 350) {
        setIsHidden(true);
      } else if (currentScrollY >= 350) {
        setIsHidden(false);
      }
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const navLinks = [
    { label: t("nav.home"), href: "#home" },
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.services"), href: "#services" },
    { label: t("nav.partners"), href: "#partners" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  const handleCatalogClick = () => {
    setIsMenuOpen(false);
    navigate(`/${language}/${routes.catalog[language]}`);
  };

  const languages = [
    { code: "pl" as Language, flag: "🇵🇱", label: "PL", title: "Polski" },
    { code: "en" as Language, flag: "🇬🇧", label: "EN", title: "English" },
    { code: "de" as Language, flag: "🇩🇪", label: "DE", title: "Deutsch" },
  ];

  const currentLangObj = languages.find(l => l.code === language) || languages[0];

// Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsMobilePartsOpen(false);
  }, [pageContext.urlPathname]);

// Prefetch language variants of the current page
useEffect(() => {
  const otherLangs: Language[] = ["pl", "en", "de"].filter(l => l !== language) as Language[];
  const currentRouteKey = getCurrentRouteKey(pageContext.urlPathname, language);

  otherLangs.forEach(newLang => {
    let newPath = `/${newLang}`;
    if (currentRouteKey && currentRouteKey !== "home") {
      if (currentRouteKey === "productDetail") {
        const segments = pageContext.urlPathname.split("/").filter(Boolean);
        const currentSlug = segments[segments.length - 1];
        newPath = `/${newLang}/${routes.catalog[newLang]}/${currentSlug}`;
      } else {
        const routePath = getRoute(currentRouteKey, newLang);
        newPath = routePath ? `/${newLang}/${routePath}` : `/${newLang}`;
      }
    }
    newPath = newPath.replace(/\/+$/, "") || "/";
    prefetch(newPath);
  });
}, [pageContext.urlPathname, language, getCurrentRouteKey]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${isHidden ? "-translate-y-full" : "translate-y-0"}`}>
      <div className="bg-primary text-primary-foreground h-10 flex items-center">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a
              href="mailto:biuro@gendera-service.pl"
              className="flex items-center gap-2 hover:text-accent transition-colors"
              aria-label="Email: biuro@gendera-service.pl"
            >
              <Mail className="w-4 h-4" size={16} aria-hidden="true" />
              <span className="hidden sm:inline">biuro@gendera-service.pl</span>
            </a>
            <a
              href="tel:+48502625857"
              className="flex items-center gap-2 hover:text-accent transition-colors"
              aria-label="Phone: +48 502 625 857"
            >
              <Phone className="w-4 h-4" size={16} aria-hidden="true" />
              <span>+48 502 625 857</span>
            </a>
          </div>

          <div className="flex items-center gap-2">
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="flex items-center gap-2 px-2 py-1 rounded bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors focus:outline-none min-w-[60px] justify-center"
                  aria-label="Change language"
                >
                  <span className="text-xs">{currentLangObj.flag}</span>
                  <span className="text-xs uppercase font-mono">{currentLangObj.label}</span>
                  <ChevronDown className="w-3 h-3 opacity-70" size={12} aria-hidden="true" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-muted border-border">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center gap-3 cursor-pointer ${language === lang.code ? "text-accent" : "text-foreground"}`}
                      aria-label={`Change language to ${lang.title}`}
                    >
                      <span>{lang.flag}</span>
                      <span className="text-sm">{lang.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="hidden sm:flex items-center gap-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`flex items-center justify-center gap-1 w-12 py-1 rounded transition-colors ${language === lang.code ? "bg-primary-foreground/20" : "hover:bg-primary-foreground/10"}`}
                  aria-label={`Change language to ${lang.title}`}
                  title={lang.title}
                >
                  <span className="text-xs leading-none">{lang.flag}</span>
                  <span className="text-xs font-medium uppercase leading-none">{lang.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <nav className="bg-background/95 backdrop-blur-sm shadow-md transition-all duration-300">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            <a href={`${homePath}#home`} onMouseEnter={handleNavMouseEnter} onClick={(e) => handleNavClick(e, "#home")} className="flex items-center cursor-pointer" aria-label="Gendera Service Home">
              <span className="text-2xl font-bold text-primary">GENDERA</span>
              <span className="text-2xl font-light ml-1 text-muted-foreground">SERVICE</span>
            </a>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={`${homePath}${link.href}`}
                  onMouseEnter={handleNavMouseEnter}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="font-medium text-foreground hover:text-accent transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={handleCatalogClick}
                className="font-medium text-foreground hover:text-accent transition-colors"
              >
                {t("nav.catalog")}
              </button>
              <div
                className="relative"
                onMouseEnter={() => setIsPartsOpen(true)}
                onMouseLeave={() => setIsPartsOpen(false)}
              >
                <button
                  className="flex items-center gap-1 font-medium text-foreground hover:text-accent transition-colors"
                  aria-haspopup="true"
                  aria-expanded={isPartsOpen}
                >
                  {t("nav.parts")}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isPartsOpen ? "rotate-180" : ""}`} size={16} aria-hidden="true" />
                </button>
                {isPartsOpen && (
                  <div className="absolute top-full right-0 pt-2">
                    <div className="bg-background shadow-lg rounded-lg py-2 min-w-[200px] z-50 border">
                      <a href="https://allegro.pl/uzytkownik/gendera-service" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:bg-secondary transition-colors">Allegro</a>
                      <a href="https://gendera-service.autoline.com.pl/stock/" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:bg-secondary transition-colors">Autoline</a>
                      <a href="https://gendera-service.otomoto.pl/inventory" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:bg-secondary transition-colors">Otomoto</a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              className="lg:hidden p-2 text-foreground"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="w-6 h-6" size={24} aria-hidden="true" /> : <Menu className="w-6 h-6" size={24} aria-hidden="true" />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t overflow-y-auto max-h-[calc(100vh-80px)]">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={`${homePath}${link.href}`}
                  onMouseEnter={handleNavMouseEnter}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block py-3 text-foreground hover:text-accent font-medium transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={handleCatalogClick}
                className="block py-3 text-foreground hover:text-accent font-medium transition-colors w-full text-left"
              >
                {t("nav.catalog")}
              </button>
              <div className="py-3">
                <button
                  onClick={() => setIsMobilePartsOpen(!isMobilePartsOpen)}
                  className="flex items-center justify-between w-full font-medium text-foreground hover:text-accent transition-colors"
                  aria-expanded={isMobilePartsOpen}
                >
                  {t("nav.parts")}
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMobilePartsOpen ? "rotate-180" : ""}`} size={20} aria-hidden="true" />
                </button>
                {isMobilePartsOpen && (
                  <div className="pl-4 mt-3 space-y-4 animate-fade-in">
                    <a href="https://allegro.pl/uzytkownik/gendera-service" target="_blank" rel="noopener noreferrer" className="block text-muted-foreground hover:text-accent">Allegro</a>
                    <a href="https://gendera-service.autoline.com.pl/stock/" target="_blank" rel="noopener noreferrer" className="block text-muted-foreground hover:text-accent">Autoline</a>
                    <a href="https://gendera-service.otomoto.pl/inventory" target="_blank" rel="noopener noreferrer" className="block text-muted-foreground hover:text-accent">Otomoto</a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
